import { useEffect } from "react";
import {
  ChainOfThoughtPrimitive,
  useAui,
  useAuiState,
} from "@assistant-ui/react";
import {
  AlertCircle as AlertCircleFilled,
  CheckCircle2,
  ChevronRight,
  Loader2,
} from "lucide-react";

/**
 * Collapsible reasoning group backed by assistant-ui's indexed chain-of-thought
 * scope. Each instance renders only the parts at its chronological position.
 */
export function ChainOfThoughtSection() {
  const aui = useAui();
  const collapsed = useAuiState((s) => s.chainOfThought.collapsed);
  const status = useAuiState((s) => s.chainOfThought.status);
  const isRunning = status.type === "running";

  useEffect(() => {
    aui.chainOfThought().setCollapsed(!isRunning);
  }, [aui, isRunning]);

  const isErrorLine = (text: string) => text.trimStart().startsWith("✗");

  const renderStepIcon = (text: string, running: boolean) => {
    if (running) {
      return (
        <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin text-sky-400" />
      );
    }
    if (isErrorLine(text)) {
      return (
        <AlertCircleFilled className="mt-0.5 h-3 w-3 shrink-0 text-red-400" />
      );
    }
    return (
      <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-300" />
    );
  };

  return (
    <ChainOfThoughtPrimitive.Root className="mb-3">
      <ChainOfThoughtPrimitive.AccordionTrigger
        type="button"
        className="flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs text-neutral-500 hover:text-neutral-400"
      >
        <span>Thinking</span>
        <ChevronRight
          className={`ml-0.5 h-3 w-3 transition-transform ${collapsed ? "" : "rotate-90"}`}
        />
      </ChainOfThoughtPrimitive.AccordionTrigger>

      {!collapsed && (
        <div className="mt-1.5 rounded-md border border-neutral-700/40 bg-neutral-800 p-2">
          <div className="space-y-1">
            <ChainOfThoughtPrimitive.Parts>
              {({ part }) => {
                if (part.type !== "reasoning") return null;
                const lines = splitReasoningChunks(part.text);
                return lines.map((line, index) => {
                  const lineIsRunning =
                    part.status.type === "running" &&
                    index === lines.length - 1;
                  return (
                    <div
                      key={`${index}:${line}`}
                      className="flex items-start gap-1.5"
                    >
                      {renderStepIcon(line, lineIsRunning)}
                      <p className="whitespace-pre-wrap text-[10px] leading-relaxed text-neutral-300">
                        {line}
                      </p>
                    </div>
                  );
                });
              }}
            </ChainOfThoughtPrimitive.Parts>
          </div>
        </div>
      )}
    </ChainOfThoughtPrimitive.Root>
  );
}

function splitReasoningChunks(content: string): string[] {
  return content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}
